import { useEffect, useState } from "react"
import { Alert, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { navigate } from "lib/routes/navigate"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { Sortable } from "lib/@components/Sortable"
import { generate } from "library/guid/index.mjs"
import { Bound } from "lib/@components/binding/Bound"
import { updateRuleSet } from "routes/rule-sets/controller/update-rule-set"
import { cloneRuleSet } from "routes/rule-sets/controller/clone-rule-set"
import { archiveRuleSet } from "routes/rule-sets/controller/archive-rule-set"
import { regimeConditionData } from "routes/rule-sets/controller/regime-condition-data"
import { assetRegisterConditionData } from "routes/rule-sets/controller/asset-register-condition-data"
import { ruleSets } from "routes/rule-sets/controller/rule-sets"
import { CONDITION_FIELDS } from "./condition-field-config"
import { AddRuleButton } from "./add-rule-button"
import { AddRuleSetReferenceButton } from "./add-rule-set-reference-button"
import { RuleSetItem } from "./rule-set-item"
import { RuleSetPreviewPanel } from "./rule-set-preview-panel"
import { validateRuleSet } from "./validate-rule-set"
import { ErrorAlert } from "./error-alert"
import { WarningAlert } from "./warning-alert"
import { AiRuleHelper } from "./ai-rule-helper"
import { RuleSetEditDetailsDialog } from "./rule-set-edit-details-dialog"
import { RuleDetailsAccordion } from "./rule-details-accordion"
import { loadRuleSetContext, saveRuleSetContext } from "./rule-set-context-storage"

export function RuleSetEditor({ ruleSet, id, menuAnchor, setMenuAnchor, readOnly }) {
    const [name, setName] = useState(ruleSet.name)
    const [description, setDescription] = useState(ruleSet.description || "")
    const [defaultAction, setDefaultAction] = useState(ruleSet.defaultAction || "include")
    const [rules, setRules] = useState(toEditableRules)
    const [saveError, setSaveError] = useState(null)
    const storedContext = loadRuleSetContext(id)
    const [selectedRegime, setSelectedRegime] = useState(storedContext.regime)
    const [selectedRegister, setSelectedRegister] = useState(storedContext.assetRegister)

    const regimeData = regimeConditionData.useResults(selectedRegime ? { regimeId: selectedRegime.id } : null)
    const registerData = assetRegisterConditionData.useResults(
        selectedRegister ? { registerId: selectedRegister.id } : null
    )

    const allRuleSets = ruleSets.useResults({ skip: 0, take: 500, status: "active" })

    const mergedRegimeData = mergeRegimeAndRegisterData()

    const openEditDetailsDialog = useDialog(<RuleSetEditDetailsDialog />, { maxWidth: "sm", fullWidth: true })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resetFromServer, [ruleSet])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(persistContext, [selectedRegime, selectedRegister])

    const validation = validateRuleSet({ name, description, defaultAction, rules })

    return (
        <Bound
            conditionFields={CONDITION_FIELDS}
            regimeData={mergedRegimeData}
            availableRuleSets={allRuleSets?.items ?? []}
            currentRuleSetId={id}
            rules={rules}
        >
            <If value={!readOnly}>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                    <MenuItem onClick={handleEditDetails}>
                        <ListItemIcon>
                            <Iconify icon="solar:pen-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Edit Details</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDuplicate}>
                        <ListItemIcon>
                            <Iconify icon="solar:copy-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Duplicate</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleArchive}>
                        <ListItemIcon>
                            <Iconify icon="solar:archive-bold" width={20} />
                        </ListItemIcon>
                        <ListItemText>Archive</ListItemText>
                    </MenuItem>
                </Menu>
            </If>
            <Stack data-cy="rule-set-editor" spacing={2} sx={{ px: 3, pt: 1, pb: 4, flex: 1 }}>
                <RuleDetailsAccordion
                    defaultAction={defaultAction}
                    regime={selectedRegime}
                    assetRegister={selectedRegister}
                    onEdit={getEditDetailsAction()}
                />
                <Divider />
                <Stack spacing={0.5}>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: "28px" }}>Rules</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Rules are evaluated in order. The first matching rule determines the action for each task
                    </Typography>
                </Stack>
                <If value={!readOnly}>
                    <AiRuleHelper onApplyRules={handleApplyAiRules} conditionFields={CONDITION_FIELDS} />
                </If>
                <If value={validation.errors.length > 0}>
                    <Repeat list={validation.errors} item={<ErrorAlert />} />
                </If>
                <If value={validation.warnings.length > 0}>
                    <Repeat list={validation.warnings} item={<WarningAlert />} />
                </If>
                <Stack spacing={1}>
                    <If value={!readOnly}>
                        <Sortable items={rules} id="id" onOrdered={handleReorder}>
                            <Repeat
                                list={rules}
                                item={
                                    <RuleSetItem
                                        onUpdate={handleUpdateRule}
                                        onRemove={handleRemoveRule}
                                        onDuplicate={handleDuplicateRule}
                                    />
                                }
                            />
                        </Sortable>
                        <Stack direction="row" spacing={1}>
                            <AddRuleButton onAdd={handleAddRule} />
                            <AddRuleSetReferenceButton onAdd={handleAddRule} />
                        </Stack>
                    </If>
                    <If value={readOnly}>
                        <Repeat list={rules} item={<RuleSetItem readOnly />} />
                    </If>
                </Stack>
                <If value={saveError}>
                    <Alert severity="error">{saveError}</Alert>
                </If>
                <Divider sx={{ borderStyle: "dashed", my: 2 }} />
                <RuleSetPreviewPanel ruleSetId={id} />
            </Stack>
        </Bound>
    )

    function mergeRegimeAndRegisterData() {
        if (!regimeData && !registerData) return null
        const base = regimeData || {
            facilities: [],
            towns: [],
            postcodes: [],
            assetTypes: [],
            regimeFields: [],
            assetRegisterFields: [],
        }
        if (!registerData?.assetRegisterFields?.length) return base
        return {
            ...base,
            assetRegisterFields: [...(base.assetRegisterFields || []), ...registerData.assetRegisterFields],
        }
    }

    function toEditableRules() {
        const editableRules = (ruleSet.rules || []).map(toEditableRule)
        if (ruleSet.parentRuleSetId && !hasRuleSetReference(editableRules, ruleSet.parentRuleSetId)) {
            editableRules.unshift({
                id: `legacy-parent-${ruleSet.parentRuleSetId}`,
                type: "ruleSetReference",
                enabled: true,
                ruleSetId: ruleSet.parentRuleSetId,
                ruleSetName: ruleSet.parentRuleSetName,
            })
        }
        return editableRules
    }

    function toEditableRule(r) {
        return {
            ...r,
            type: r.type || "rule",
            enabled: r.enabled !== false,
            conditions: (r.conditions || []).map((c) => ({ _id: generate(), ...c })),
            action: r.action ? { ...r.action } : { type: "include" },
        }
    }

    function resetFromServer() {
        setName(ruleSet.name)
        setDescription(ruleSet.description || "")
        setDefaultAction(ruleSet.defaultAction || "include")
        setRules(toEditableRules())
        setSaveError(null)
    }

    function persistContext() {
        saveRuleSetContext(id, { regime: selectedRegime, assetRegister: selectedRegister })
    }

    function handleReorder(reordered) {
        if (readOnly) return
        setRules(reordered)
        persistRules(reordered)
    }

    function handleAddRule(newRule) {
        if (readOnly) return
        setRules((prev) => [...prev, { ...newRule, enabled: true }])
    }

    function handleDuplicateRule(rule) {
        if (readOnly) return
        const copy = {
            ...rule,
            id: generate(),
            conditions: (rule.conditions || []).map((c) => ({ ...c, _id: generate() })),
            action: rule.action ? { ...rule.action } : { type: "include" },
        }
        setRules((prev) => {
            const index = findRuleIndex(prev, rule.id)
            const next = [...prev]
            next.splice(index + 1, 0, copy)
            persistRules(next)
            return next
        })
    }

    function handleApplyAiRules(generatedRules) {
        if (readOnly) return
        setRules((prev) => {
            const next = [...prev, ...generatedRules.map(normalizeAiRule)]
            persistRules(next)
            return next
        })
    }

    function normalizeAiRule(rule) {
        return {
            ...rule,
            enabled: true,
            conditions: (rule.conditions || []).map(normalizeAiCondition),
        }
    }

    function normalizeAiCondition(condition) {
        if (condition.field === "frequency" && condition.value?.period) {
            const periodMap = {
                week: "Week",
                month: "Month",
                year: "Year",
                weekly: "Week",
                monthly: "Month",
                yearly: "Year",
            }
            const normalized = periodMap[condition.value.period.toLowerCase()] || condition.value.period
            return { ...condition, value: { ...condition.value, period: normalized } }
        }
        return condition
    }

    function handleUpdateRule(ruleId, updatedRule) {
        if (readOnly) return
        setRules((prev) => {
            const next = applyRuleUpdate(prev, ruleId, updatedRule)
            persistRules(next)
            return next
        })
    }

    function handleRemoveRule(ruleId) {
        if (readOnly) return
        setRules((prev) => {
            const next = removeRuleById(prev, ruleId)
            persistRules(next)
            return next
        })
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    async function handleEditDetails() {
        if (readOnly) return
        closeMenu()
        const result = await openEditDetailsDialog({
            currentRuleSetId: id,
            name,
            description,
            defaultAction,
            regime: selectedRegime,
            assetRegister: selectedRegister,
        })
        if (!result) return
        const newName = result.name.trim()
        const newDescription = result.description || ""
        const newDefaultAction = result.includeByDefault === false ? "exclude" : "include"
        setName(newName)
        setDescription(newDescription)
        setDefaultAction(newDefaultAction)
        setSelectedRegime(result.regime || null)
        setSelectedRegister(result.assetRegister || null)
        saveRuleSetContext(id, {
            regime: result.regime || null,
            assetRegister: result.assetRegister || null,
        })
        try {
            await busyWhile(
                () =>
                    updateRuleSet({
                        id,
                        data: {
                            name: newName,
                            description: newDescription,
                            defaultAction: newDefaultAction,
                            parentRuleSetId: null,
                            rules: stripTypenames(rules),
                        },
                    }),
                "Saving rule set..."
            )
            successSnackbar("Rule set updated")
        } catch (e) {
            setSaveError(e.message || "Failed to save rule set")
        }
    }

    async function handleDuplicate() {
        if (readOnly) return
        closeMenu()
        const newSet = await busyWhile(duplicateThisRuleSet, "Duplicating rule set...")
        if (newSet?.id) {
            navigate(`/app/optimiser/rule-sets/${newSet.id}`)
            successSnackbar("Rule set duplicated")
        }
    }

    function duplicateThisRuleSet() {
        return cloneRuleSet({ id })
    }

    async function handleArchive() {
        if (readOnly) return
        closeMenu()
        const ok = await confirm(
            `Archive "${name}"? It can be restored later from the Archived filter.`,
            "Archive Rule Set"
        )
        if (!ok) return
        await busyWhile(() => archiveRuleSet({ id }), "Archiving rule set...")
        navigate("/app/optimiser?optimiserTab=rule-sets")
        successSnackbar(`Rule set "${name}" archived`)
    }

    async function persistRules(nextRules) {
        if (readOnly) return
        setSaveError(null)
        try {
            await updateRuleSet({
                id,
                data: {
                    name,
                    description,
                    defaultAction,
                    parentRuleSetId: null,
                    rules: stripTypenames(nextRules),
                },
            })
        } catch (e) {
            setSaveError(e.message || "Failed to save rule set")
        }
    }

    function stripTypenames(rules) {
        return rules.map(({ __typename, conditions, action, ruleSetName: __ruleSetName, ...rest }) => ({
            ...rest,
            conditions: (conditions || []).map(({ __typename, _id: __id, ...c }) => c),
            action: action ? (({ __typename, ...a }) => a)(action) : action,
        }))
    }

    function hasRuleSetReference(sourceRules, ruleSetId) {
        return sourceRules.some((rule) => rule.type === "ruleSetReference" && rule.ruleSetId === ruleSetId)
    }

    function findRuleIndex(sourceRules, ruleId) {
        return sourceRules.findIndex((r) => r.id === ruleId)
    }

    function applyRuleUpdate(sourceRules, ruleId, updatedRule) {
        return sourceRules.map((r) => (r.id === ruleId ? { ...r, ...updatedRule } : r))
    }

    function removeRuleById(sourceRules, ruleId) {
        return sourceRules.filter((r) => r.id !== ruleId)
    }

    function getEditDetailsAction() {
        if (readOnly) return undefined
        return handleEditDetails
    }
}
